import { Box } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import cn from 'classnames';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  name: string;
  label?: string;
  onChange?: any;
  disabled?: boolean;
  value?: any;
  required?: boolean;
}

const DateTextField = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      label,
      onChange,
      disabled = false,
      value,
      required,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
      if (errors && name) {
        const nameArray = name.split('.');
        const error = nameArray.length > 1 ? errors[nameArray[0]]?.[nameArray[1]]?.message : errors[name]?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    return (
      <Box className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            className={cn(styles.input, inputClass)}
            {...(register ? register(name, registerOptions) : {})}
            views={['year', 'month', 'day']}
            inputRef={inputRef}
            ref={ref}
            label={label}
            disabled={disabled}
            onChange={onChange}
            value={value}
            {...props}
            data-testid={`denta-history-${label}`}
            desktopModeMediaQuery="@media (min-width: 0px)"
            slotProps={{
              textField: {
                error: !!errorMessage,
                helperText: errorMessage || '',
                inputProps: {
                  'data-testid': `denta-history-${name}`,
                  'aria-required': required || undefined,
                },
                InputLabelProps: {
                  required: Boolean(required),
                  shrink: Boolean(value) || undefined,
                  sx: {
                    fontSize: '14px',
                    backgroundColor: 'white',
                    px: 0.5,
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(6px, -10px) scale(0.75)',
                    },
                    '& .MuiFormLabel-asterisk': {
                      color: '#d32f2f',
                      opacity: 1,
                      fontWeight: 700,
                    },
                  },
                },
                sx: {
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                    padding: 0,
                  },
                },
              },
              popper: {
                sx: { zIndex: 10000 },
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    );
  },
);

export default DateTextField;
